import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

/**
 * Тема приложения (ТЗ 18.5): основной цвет с логотипа, тёплые поверхности и скругления
 * по утверждённым макетам (артефакт «Домашняя библиотека — макеты»).
 */
/** Акцент тёмной темы, латунь (выбор владельца 21 октября 2026): кнопки, ссылки, активные пункты, рамка фокуса. */
const DARK_ACCENT = { color: '#d2b17a', hover: '#dcc195', active: '#e6d1b0' }

export const AppPreset = definePreset(Aura, {
  // Скругления крупнее, чем в Aura: поля и карточки на макетах 12–14 px.
  primitive: {
    borderRadius: { none: '0', xs: '4px', sm: '6px', md: '10px', lg: '12px', xl: '14px' },
  },
  semantic: {
    // Синий корешок с логотипа (#437aaa).
    primary: {
      50: '#f0f4f8',
      100: '#e1eaf1',
      200: '#c3d4e4',
      300: '#9dbad3',
      400: '#709abe',
      500: '#437aaa',
      600: '#3b6a95',
      700: '#335b7f',
      800: '#2b4b6a',
      900: '#233d57',
      950: '#1c3044',
    },
    colorScheme: {
      // Тёплый фон в светлой теме, как у логотипа; в тёмной синевато-серый.
      light: {
        // Основной цвет на шаг темнее логотипа: #437aaa на тёплом фоне даёт контраст 4.15,
        // меньше 4.5:1 для текста (WCAG 1.4.3). Кнопки, ссылки и вкладки берут его отсюда.
        primary: {
          color: '{primary.600}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
        // Тёплые поверхности и тёмно-синий текст из утверждённых макетов (14.1).
        surface: {
          0: '#ffffff',
          50: '#f7f4ee',
          100: '#f1ede5',
          200: '#ece6da',
          300: '#e4ded2',
          400: '#d8d1c3',
          // Aura берёт 500 для приглушённого текста (переключатели, вторичные кнопки):
          // тёплый серый с контрастом 4.6 даже на фоне 100.
          500: '#6f6a62',
          600: '#5f6b78',
          700: '#4a5562',
          800: '#2e3e50',
          900: '#24364a',
          950: '#1a2836',
        },
        text: {
          color: '#24364a',
          hoverColor: '#1a2836',
          mutedColor: '#5f6b78',
          hoverMutedColor: '#4a5562',
        },
        formField: {
          borderColor: '{surface.400}',
          // Подсказки в полях читаются с контрастом 4.5:1 и на белом, и на тёплом фоне.
          placeholderColor: '{surface.500}',
          floatLabelColor: '{surface.500}',
        },
      },
      dark: {
        // Тёплый графит (выбор владельца 21 октября 2026): поверхности stone, как тёплый
        // фон светлой темы, и тёплый акцент вместо синего для кнопок и активных элементов.
        surface: {
          0: '#ffffff',
          50: '{stone.50}',
          100: '{stone.100}',
          200: '{stone.200}',
          300: '{stone.300}',
          400: '{stone.400}',
          500: '{stone.500}',
          600: '{stone.600}',
          700: '{stone.700}',
          800: '{stone.800}',
          900: '{stone.900}',
          950: '{stone.950}',
        },
        primary: {
          color: DARK_ACCENT.color,
          contrastColor: '{stone.950}',
          hoverColor: DARK_ACCENT.hover,
          activeColor: DARK_ACCENT.active,
        },
        highlight: {
          background: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 84%)`,
          focusBackground: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 76%)`,
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
  // Вкладки на фоне страницы, как на макетах (места и участники библиотеки, карточка книги).
  components: {
    // Текст сообщений в светлой теме темнее, чем в Aura: оттенки 600 на бледном фоне
    // не дотягивают до контраста 4.5:1 (WCAG 1.4.3, задача 4.5).
    message: {
      colorScheme: {
        light: {
          info: {
            color: '{blue.700}',
            outlined: { color: '{blue.700}' },
            simple: { color: '{blue.700}' },
          },
          success: {
            color: '{green.700}',
            outlined: { color: '{green.700}' },
            simple: { color: '{green.700}' },
          },
          warn: {
            color: '{yellow.800}',
            outlined: { color: '{yellow.800}' },
            simple: { color: '{yellow.800}' },
          },
          error: {
            color: '{red.700}',
            outlined: { color: '{red.700}' },
            simple: { color: '{red.700}' },
          },
          secondary: { outlined: { color: '{surface.600}' }, simple: { color: '{surface.600}' } },
        },
      },
    },
    // Кнопки в светлой теме: цветной текст и белый текст на цветном фоне с контрастом
    // не меньше 4.5:1. У Aura оттенки 500 дают 2.3–3.8.
    button: {
      colorScheme: {
        light: {
          root: {
            info: {
              background: '{sky.700}',
              hoverBackground: '{sky.800}',
              activeBackground: '{sky.900}',
              borderColor: '{sky.700}',
            },
            success: {
              background: '{green.700}',
              hoverBackground: '{green.800}',
              activeBackground: '{green.900}',
              borderColor: '{green.700}',
            },
            warn: {
              background: '{orange.700}',
              hoverBackground: '{orange.800}',
              activeBackground: '{orange.900}',
              borderColor: '{orange.700}',
            },
            help: {
              background: '{purple.600}',
              hoverBackground: '{purple.700}',
              activeBackground: '{purple.800}',
              borderColor: '{purple.600}',
            },
            danger: {
              background: '{red.600}',
              hoverBackground: '{red.700}',
              activeBackground: '{red.800}',
              borderColor: '{red.600}',
            },
          },
          outlined: {
            secondary: { color: '{surface.600}' },
            success: { color: '{green.700}' },
            info: { color: '{sky.700}' },
            warn: { color: '{orange.700}' },
            help: { color: '{purple.600}' },
            danger: { color: '{red.700}' },
          },
          text: {
            secondary: { color: '{surface.600}' },
            success: { color: '{green.700}' },
            info: { color: '{sky.700}' },
            warn: { color: '{orange.700}' },
            help: { color: '{purple.600}' },
            danger: { color: '{red.700}' },
          },
        },
        // Обводка и подсветка второстепенных кнопок тем же тёплым акцентом, а не синим.
        dark: {
          outlined: {
            primary: {
              borderColor: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 55%)`,
              hoverBackground: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 92%)`,
              activeBackground: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 84%)`,
            },
          },
          text: {
            primary: {
              hoverBackground: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 92%)`,
              activeBackground: `color-mix(in srgb, ${DARK_ACCENT.color}, transparent 84%)`,
            },
          },
        },
      },
    },
    tabs: {
      tablist: { background: 'transparent' },
      tab: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
      },
      tabpanel: { background: 'transparent', padding: '1rem 0 0' },
    },
  },
})

export const primeVueThemeOptions = {
  preset: AppPreset,
  options: {
    darkModeSelector: '.app-dark',
    cssLayer: { name: 'primevue', order: 'app-styles, primevue' },
  },
}
